import {
  AUTO_ANALYSIS_MINUTES_PER_LESSON,
  LESSON_MINUTES,
} from '../constants/autoAnalysis';

/**
 * Tutopiya AI lesson analysis pricing.
 *
 * Each analysed lesson consumes `AUTO_ANALYSIS_MINUTES_PER_LESSON` minutes of
 * lesson credit on top of the lesson itself. Rather than letting that eat into
 * the lessons the student just paid for, checkout sells those minutes upfront:
 * the add-on is priced as a fraction of the per-lesson rate and the backend
 * grants the extra credit with the purchase, so the post-lesson deduction is
 * already funded.
 *
 * Must stay in sync with the backend, which prices the same add-on as its own
 * Stripe line item.
 */

/** Fraction of a lesson that one analysis costs (15 / 60 = 0.25). */
export const AUTO_ANALYSIS_LESSON_FRACTION =
  AUTO_ANALYSIS_MINUTES_PER_LESSON / LESSON_MINUTES;

/**
 * Per-lesson price of the analysis add-on, derived from the per-lesson rate
 * *after* discounts so the add-on inherits the plan's discount.
 *
 * Rounded to 2 decimals before it is multiplied by the lesson count, the same
 * way the tutoring rate is, because it is a real money value Stripe charges as
 * `unit_amount x quantity` — multiplying an un-rounded rate would drift from the
 * charged total by a few cents on larger plans.
 */
export const getAutoAnalysisRatePerLesson = (
  perLessonRate: number | undefined
): number => {
  if (!perLessonRate || !Number.isFinite(perLessonRate)) return 0;

  return parseFloat((perLessonRate * AUTO_ANALYSIS_LESSON_FRACTION).toFixed(2));
};

/**
 * Money added to the checkout total for the analysis add-on.
 *
 * `billingMonths` is 1 for monthly plans and one-time top-ups, 3 for quarterly
 * plans — matching how the tutoring total is derived.
 */
export const getAutoAnalysisAmount = (data: {
  perLessonRate: number | undefined;
  lessons: number;
  billingMonths?: number;
}): number => {
  const { perLessonRate, lessons, billingMonths = 1 } = data;

  if (!lessons || lessons <= 0) return 0;

  return getAutoAnalysisRatePerLesson(perLessonRate) * lessons * billingMonths;
};

/**
 * Extra lesson credit (in minutes) the purchase must grant so every paid lesson
 * can be analysed without shortening the student's tutoring time.
 */
export const getAutoAnalysisCreditMinutes = (data: {
  lessons: number;
  billingMonths?: number;
}): number => {
  const { lessons, billingMonths = 1 } = data;

  if (!lessons || lessons <= 0) return 0;

  return lessons * billingMonths * AUTO_ANALYSIS_MINUTES_PER_LESSON;
};

export default getAutoAnalysisAmount;
